import React from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, Shuffle, Repeat, Repeat1, ListMusic, X } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { fileStreamUrl } from "../lib/api";
import { Link } from "react-router-dom";

const formatTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

export const AudioPlayerBar = () => {
  const {
    currentTrack, isPlaying, progress, duration, volume, queue,
    shuffle, repeat, showQueue,
    setVolume, setShowQueue,
    togglePlay, next, prev, seek, toggleShuffle, toggleRepeat,
    playTrack, removeFromQueue,
  } = usePlayer();

  if (!currentTrack) return null;

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <>
      {showQueue && (
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setShowQueue(false)} data-testid="queue-backdrop" />
      )}
      {showQueue && (
        <aside className="fixed right-0 top-0 bottom-24 z-50 w-full md:w-96 bg-brand-surface border-l border-white/10 overflow-y-auto" data-testid="queue-panel">
          <div className="flex items-center justify-between p-4 border-b border-white/5 sticky top-0 bg-brand-surface z-10">
            <h3 className="font-display text-lg font-black inline-flex items-center gap-2">
              <ListMusic size={18} /> File d'attente ({queue.length})
            </h3>
            <button onClick={() => setShowQueue(false)} className="text-white/50 hover:text-white" data-testid="queue-close">
              <X size={18} />
            </button>
          </div>
          <div className="p-2">
            {queue.length === 0 ? (
              <p className="text-white/50 text-sm text-center py-8">La file d'attente est vide.</p>
            ) : (
              queue.map((t) => (
                <div
                  key={t.track_id}
                  className={`flex items-center gap-2 p-2 rounded-xl group ${
                    currentTrack?.track_id === t.track_id ? "bg-brand-red/10" : "hover:bg-white/5"
                  }`}
                  data-testid={`queue-item-${t.track_id}`}
                >
                  <button onClick={() => playTrack(t, queue)} className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-brand-elevated">
                    {t.cover_path ? (
                      <img src={fileStreamUrl(t.cover_path)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-brand-red to-brand-yellow" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => playTrack(t, queue)}>
                    <p className="text-sm font-semibold truncate">{t.title}</p>
                    <p className="text-xs text-white/50 truncate">{t.artist_name}</p>
                  </div>
                  <button onClick={() => removeFromQueue(t.track_id)} className="text-white/30 hover:text-red-400 opacity-0 group-hover:opacity-100" data-testid={`queue-remove-${t.track_id}`}>
                    <X size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>
      )}

      <div className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-white/10" data-testid="audio-player-bar">
        {/* progress */}
        <div
          className="h-1 bg-white/10 cursor-pointer"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            seek((e.clientX - rect.left) / rect.width);
          }}
          data-testid="player-progress"
        >
          <div
            className="h-full bg-gradient-to-r from-brand-red via-brand-orange to-brand-yellow transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="flex items-center gap-3 md:gap-4 px-3 md:px-6 py-3">
          <Link
            to={`/track/${currentTrack.track_id}`}
            className="flex items-center gap-3 min-w-0 flex-1 md:flex-none md:w-64"
            data-testid="player-track-info"
          >
            <div className="w-11 h-11 rounded-lg overflow-hidden bg-brand-elevated shrink-0">
              {currentTrack.cover_path ? (
                <img src={fileStreamUrl(currentTrack.cover_path)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-brand-red to-brand-yellow" />
              )}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{currentTrack.title}</p>
              <p className="text-xs text-white/60 truncate">{currentTrack.artist_name}</p>
            </div>
          </Link>

          <div className="flex-1 flex flex-col items-center gap-1">
            <div className="flex items-center gap-1 md:gap-2">
              <button
                onClick={toggleShuffle}
                className={`hidden md:inline p-2 rounded-lg transition-colors ${shuffle ? "text-brand-red" : "text-white/50 hover:text-white"}`}
                data-testid="player-shuffle-btn"
                title="Shuffle"
              >
                <Shuffle size={16} />
              </button>
              <button onClick={prev} className="text-white/70 hover:text-white transition-colors p-1.5" data-testid="player-prev-btn">
                <SkipBack size={18} />
              </button>
              <button
                onClick={togglePlay}
                className="w-10 h-10 rounded-full bg-white text-brand-dark flex items-center justify-center hover:scale-105 transition-transform"
                data-testid="player-play-btn"
              >
                {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
              </button>
              <button onClick={next} className="text-white/70 hover:text-white transition-colors p-1.5" data-testid="player-next-btn">
                <SkipForward size={18} />
              </button>
              <button
                onClick={toggleRepeat}
                className={`hidden md:inline p-2 rounded-lg transition-colors ${repeat !== "off" ? "text-brand-red" : "text-white/50 hover:text-white"}`}
                data-testid="player-repeat-btn"
                title={`Repeat: ${repeat}`}
              >
                {repeat === "one" ? <Repeat1 size={16} /> : <Repeat size={16} />}
              </button>
            </div>
            <div className="hidden md:flex items-center gap-2 text-[11px] text-white/50 font-mono">
              <span>{formatTime(progress)}</span>
              <span>/</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2 w-40">
            <Volume2 size={16} className="text-white/60" />
            <input
              type="range"
              min="0" max="1" step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-full accent-brand-red"
              data-testid="player-volume"
            />
          </div>

          <button
            onClick={() => setShowQueue(!showQueue)}
            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 relative"
            data-testid="player-queue-btn"
            title="File d'attente"
          >
            <ListMusic size={18} />
            {queue.length > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-brand-red text-white text-[9px] font-bold flex items-center justify-center">
                {queue.length}
              </span>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default AudioPlayerBar;
